import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { hrApi } from '../api';
import type { PerformanceReview } from '../types';

const RATINGS = [1, 2, 3, 4, 5];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Present when editing an existing draft; absent when creating a new one. */
  review?: PerformanceReview | null;
  /** Fixed employee to create the review for (create-mode only). */
  employeeId?: string;
  employeeName?: string;
  onSaved: () => void;
}

/** Create/edit a draft performance review. Only drafts can be edited — the
 *  page never opens this dialog for a shared/acknowledged review (those
 *  render read-only). */
export function ReviewDialog({ open, onOpenChange, review, employeeId, employeeName, onSaved }: Props) {
  const isEdit = !!review;
  const [periodStart, setPeriodStart] = React.useState('');
  const [periodEnd, setPeriodEnd] = React.useState('');
  const [rating, setRating] = React.useState('5');
  const [strengths, setStrengths] = React.useState('');
  const [improvements, setImprovements] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPeriodStart(review?.period_start ?? '');
    setPeriodEnd(review?.period_end ?? '');
    setRating(String(review?.overall_rating ?? 5));
    setStrengths(review?.strengths ?? '');
    setImprovements(review?.improvements ?? '');
    setSummary(review?.summary ?? '');
  }, [open, review]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!periodStart || !periodEnd) { toast.error('Period start and end are required'); return; }
    setBusy(true);
    try {
      if (isEdit && review) {
        await hrApi.patchReviewDraft(review.id, {
          period_start: periodStart,
          period_end: periodEnd,
          overall_rating: Number(rating),
          strengths: strengths.trim() || null,
          improvements: improvements.trim() || null,
          summary: summary.trim() || null,
        });
        toast.success('Draft updated');
      } else {
        if (!employeeId) { toast.error('Pick an employee first'); setBusy(false); return; }
        await hrApi.createReviewDraft({
          employee_id: employeeId,
          period_start: periodStart,
          period_end: periodEnd,
          overall_rating: Number(rating),
          strengths: strengths.trim() || null,
          improvements: improvements.trim() || null,
          summary: summary.trim() || null,
        });
        toast.success('Draft created');
      }
      onSaved();
    } catch (err: any) {
      toast.error(isEdit ? 'Could not update draft' : 'Could not create draft', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit review draft' : `New review draft${employeeName ? ` — ${employeeName}` : ''}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-4 pt-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Period start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Period end</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Overall rating</Label>
            <Select value={rating} onValueChange={setRating}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RATINGS.map((r) => <SelectItem key={r} value={String(r)}>{r} / 5</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Strengths</Label>
            <Textarea value={strengths} onChange={(e) => setStrengths(e.target.value)} rows={2} placeholder="What's going well" />
          </div>
          <div className="grid gap-1.5">
            <Label>Areas to improve</Label>
            <Textarea value={improvements} onChange={(e) => setImprovements(e.target.value)} rows={2} placeholder="What to work on" />
          </div>
          <div className="grid gap-1.5">
            <Label>Summary</Label>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} placeholder="Overall takeaway" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : isEdit ? 'Save changes' : 'Save draft'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
